/**
 * Regra única para a mensagem/CTA de status de conexão WhatsApp exibida
 * nos empty states de Atendimento (chat-window e conversation-list).
 *
 * Qualquer ajuste de copy ou de regra deve ser feito aqui — os componentes
 * apenas renderizam o resultado retornado.
 *
 * Tipos de CTA:
 * - 'config'      → quem PODE cadastrar a conexão e ainda não tem uma conectada;
 *                   link para settings. Vale para o gestor e, desde que a tela de
 *                   Conexões WhatsApp foi liberada para não-gestor, também para o
 *                   profissional sem conexão individual (origem 'atendimento').
 * - 'ask_gestor'  → profissional sem conexão individual em uma origem onde ele NÃO
 *                   alcança a tela de Configurações (hoje só 'solicitacoes').
 * - 'scan_qrcode' → profissional com conexão individual NÃO conectada
 *                   (ex.: status 'conectando', 'desconectado', 'erro').
 * - null          → estado OK; nenhuma mensagem precisa ser exibida.
 *
 * O backend já filtra `conexoes` pelo profissional logado (via
 * `session['id_profissional']`), então para não-gestor a presença de uma
 * conexão `tipo === 'individual'` na lista significa "é a dele".
 *
 * Formato do retorno:
 *   { type, title, description, text }
 * - `title`       → título curto em destaque (h3-like) para o empty state.
 * - `description` → frase explicando o porquê e o próximo passo.
 * - `text`        → copy legado em linha única. Mantido para compatibilidade
 *                   de consumidores antigos que renderizam o CTA num span
 *                   inline (não deve mais ser usado em novas UIs).
 */

export const CONEXAO_CTA_TYPE = {
  CONFIG: 'config',
  ASK_GESTOR: 'ask_gestor',
  SCAN_QRCODE: 'scan_qrcode',
}

const EMPTY_CTA = { type: null, title: '', description: '', text: '' }

// CTA do profissional (não-gestor) que ainda não tem conexão individual.
//
// A tela Configurações > Mensagens > Conexões WhatsApp deixou de ser gestor-only:
// o próprio profissional cadastra, conecta e remove a conexão dele (a conexão é do
// aparelho DELE — quem lê o QR Code é ele). Por isso "peça ao gestor" virou
// orientação falsa e o retorno passou a ser 'config', tipo que os empty states já
// renderizam com o link "Ir para Configurações" — sem tocar em template.
//
// ★ Exceção 'solicitacoes': ali o link do 'config' aponta para
// `settings-v2.solicitacoes`, rota que continua exigindo IS_GESTOR
// (`requireSolicitacoesInternal` → `requireFeatures` →
// `permissionSettingsV2(ctx, true, ...)`). Oferecer "Ir para Configurações" ao
// não-gestor nessa origem seria um item de menu levando a 404 — o oposto da regra.
// Enquanto esse destino não existir para ele, pedir ao gestor segue sendo a
// orientação verdadeira nessa tela (é o único caminho que ainda usa ASK_GESTOR).
function buildCtaSemConexaoIndividual(isSolicitacoes) {
  if (isSolicitacoes) {
    return {
      type: CONEXAO_CTA_TYPE.ASK_GESTOR,
      title: 'Peça ao gestor para cadastrar uma conexão',
      description:
        'Sua conta ainda não tem uma conexão WhatsApp individual. Solicite ao gestor do escritório para habilitar uma conexão para você começar a atender.',
      text: 'Peça para o gestor cadastrar uma conexão',
    }
  }

  return {
    type: CONEXAO_CTA_TYPE.CONFIG,
    title: 'Crie sua conexão WhatsApp',
    description:
      'Você ainda não tem uma conexão WhatsApp individual. Crie a sua nas Configurações e conecte pelo QR Code para começar a atender pelo CRM.',
    text: 'Criar minha conexão WhatsApp',
  }
}

// `origem` permite ajustar a copy ao contexto da tela compartilhada:
//  - 'atendimento' (default): copy do CRM (mensagens pelo CRM).
//  - 'solicitacoes': copy NEUTRA — a Comunicação de Solicitações não tem
//    relação com o CRM e a conexão WhatsApp é opcional nesse fluxo.
export function resolveConexaoStatusCta({
  conexoes,
  isGestor,
  origem = 'atendimento',
} = {}) {
  const list = Array.isArray(conexoes) ? conexoes : []
  const hasActive = list.some((c) => c && c.status === 'conectado')
  const isSolicitacoes = origem === 'solicitacoes'

  if (isGestor) {
    if (!hasActive) {
      return {
        type: CONEXAO_CTA_TYPE.CONFIG,
        title: 'Configure uma conexão WhatsApp',
        description: isSolicitacoes
          ? 'Cadastre uma conexão nas Configurações para começar a receber e enviar mensagens.'
          : 'Cadastre uma conexão nas Configurações para começar a receber e enviar mensagens pelo CRM.',
        text: 'Configurar conexões WhatsApp',
      }
    }
    return { ...EMPTY_CTA }
  }

  const individual = list.find((c) => c && c.tipo === 'individual')
  if (!individual) return buildCtaSemConexaoIndividual(isSolicitacoes)

  if (individual.status !== 'conectado') {
    return {
      type: CONEXAO_CTA_TYPE.SCAN_QRCODE,
      title: 'Conecte seu WhatsApp via QR Code',
      description:
        'Abra o menu de ações ⋮ acima e selecione "Conectar via QR Code" para escanear com o seu WhatsApp e começar a atender.',
      text: 'Abra o menu ⋮ acima e escaneie o QR Code para conectar seu WhatsApp.',
    }
  }

  return { ...EMPTY_CTA }
}
